import { useId, useState } from 'react';
import { Info } from 'lucide-react';
import { MADEFOR_TEXT } from './picker-fonts';

// Which "What you get" lines carry the design's ⓘ. Matched on the key SUFFIX rather than a
// per-plan map, so a new tier's bonus lines pick the tooltip up for free — and both lines share one
// note, because the thing a buyer needs told is the same for either: the allowance is not paid for
// out of the credit pool.
function featureInfoKey(featureKey: string): string | null {
  return featureKey.endsWith('.bonusRuns') || featureKey.endsWith('.bonusMeetings') ? 'pricing.info.bonus' : null;
}

// The ⓘ beside a bonus line, and the bubble it opens. Hover OR keyboard focus opens it and Escape
// closes it — a tooltip reachable only by pointer is one a keyboard user cannot read at all.
//
// The bubble is `--ds-surface-sunken-dark` on `--ink-on-fill`: BOTH flip with the theme, so it stays
// a dark bubble with light text in light mode and inverts in dark, the same pair the period pill uses.
// Naming a ramp step here instead would paint one fixed colour into both themes.
function FeatureInfo({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    // Hover and Escape are handled on the WRAPPER, not the glyph: the bubble is inside it, so moving
    // the pointer off the glyph and onto the note keeps it open (WCAG 1.4.13 hoverable), and Escape
    // lands wherever focus sits within the pair.
    <span
      style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      {/* `role="img"` + `aria-label` give the glyph its own NAME; `aria-describedby` adds the note as
          its description. A focusable element with only a description announces as nothing. */}
      <span
        tabIndex={0}
        role="img"
        aria-label={text}
        aria-describedby={id}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        style={{ display: 'inline-flex', color: 'var(--ds-text-secondary)', borderRadius: '50%' }}
      >
        <Info size={16} aria-hidden />
      </span>
      {/* Kept mounted rather than conditionally rendered, so `aria-describedby` always resolves and a
          screen reader reads the note with the line whether or not the bubble is on screen. */}
      <span
        id={id}
        role="tooltip"
        style={{
          position: 'absolute',
          bottom: 'calc(100% + 8px)',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1,
          padding: '6px 10px',
          borderRadius: 'var(--ds-radius-4)',
          background: 'var(--ds-surface-sunken-dark)',
          color: 'var(--ink-on-fill)',
          fontFamily: MADEFOR_TEXT,
          fontSize: 13,
          lineHeight: '18px',
          // Bounded and wrapping rather than `nowrap`: the note is one short line in English but half
          // the bundles are longer, and a bubble centred on a glyph near the card's edge would run
          // off it. 220 keeps every translation to one or two lines.
          maxWidth: 220,
          width: 'max-content',
          textAlign: 'center',
          opacity: open ? 1 : 0,
          visibility: open ? 'visible' : 'hidden',
          transition: 'opacity 120ms ease',
        }}
      >
        {text}
        {/* The frame's caret, drawn as a rotated corner of the same fill so it can never drift in
            colour from the bubble it hangs off. */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            width: 8,
            height: 8,
            marginTop: -4,
            transform: 'translateX(-50%) rotate(45deg)',
            background: 'var(--ds-surface-sunken-dark)',
          }}
        />
      </span>
    </span>
  );
}

export { FeatureInfo, featureInfoKey };
